/**
 * Time Slot Management Utilities
 * Provides functions for managing appointment time slots
 */

export interface TimeSlot {
  time_from: string;
  time_to: string;
}

export interface AppointmentWithTimeSlot {
  id?: number;
  dispatch_id?: number;
  user_id?: number;
  appointment_date?: string;
  time_from?: string;
  time_to?: string;
  stop_sequence?: number; // DEPRECATED
}

/**
 * Format minutes to HH:MM:SS time string
 */
export const formatMinutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
};

/**
 * Parse time string (HH:MM:SS or HH:MM) to minutes since midnight
 */
export const parseTimeToMinutes = (timeStr: string): number => {
  const parts = timeStr.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  return hours * 60 + minutes;
};

/**
 * Format time string for display (HH:MM)
 */
export const formatTimeForDisplay = (timeStr: string): string => {
  if (!timeStr) return '';
  return timeStr.substring(0, 5);
};

/**
 * Format time string (HH:MM:SS or HH:MM) to 12-hour AM/PM (e.g. "2:00 PM")
 */
export const formatTimeAmPm = (time: string): string => {
  if (!time) return '';
  const [hourStr, minStr] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const min = minStr || '00';
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  return `${h}:${min} ${suffix}`;
};

/**
 * Generate default time slots for a day
 */
export const generateDefaultTimeSlots = (
  startTime: string = '08:00:00',
  endTime: string = '17:00:00',
  slotDurationMinutes: number = 60
): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);
  
  for (let minutes = startMinutes; minutes < endMinutes; minutes += slotDurationMinutes) {
    const timeFrom = formatMinutesToTime(minutes);
    const timeTo = formatMinutesToTime(minutes + slotDurationMinutes);
    slots.push({ time_from: timeFrom, time_to: timeTo });
  }
  
  return slots;
};

/**
 * Check if two time slots overlap
 */
export const doTimeSlotsOverlap = (slot1: TimeSlot, slot2: TimeSlot): boolean => {
  const slot1Start = parseTimeToMinutes(slot1.time_from);
  const slot1End = parseTimeToMinutes(slot1.time_to);
  const slot2Start = parseTimeToMinutes(slot2.time_from);
  const slot2End = parseTimeToMinutes(slot2.time_to);
  
  return (
    (slot1Start < slot2End && slot1End > slot2Start) ||
    (slot2Start < slot1End && slot2End > slot1Start)
  );
};

/**
 * Find conflicts with existing appointments
 */
export const findTimeSlotConflicts = (
  newSlot: TimeSlot,
  existingAppointments: AppointmentWithTimeSlot[],
  excludeAppointmentId?: number
): AppointmentWithTimeSlot[] => {
  return existingAppointments.filter(appointment => {
    // Skip the appointment being updated
    if (excludeAppointmentId && appointment.id === excludeAppointmentId) {
      return false;
    }
    
    // Skip appointments without time slots
    if (!appointment.time_from || !appointment.time_to) {
      return false;
    }
    
    const existingSlot: TimeSlot = {
      time_from: appointment.time_from,
      time_to: appointment.time_to
    };
    
    return doTimeSlotsOverlap(newSlot, existingSlot);
  });
};

/**
 * Get the next available time slot after existing appointments
 */
export const getNextAvailableTimeSlot = (
  existingAppointments: AppointmentWithTimeSlot[],
  slotDurationMinutes: number = 60,
  startTime: string = '08:00:00'
): TimeSlot => {
  // Find the latest end time
  let latestEndTime = startTime;
  
  existingAppointments.forEach(appointment => {
    if (appointment.time_to && appointment.time_to > latestEndTime) {
      latestEndTime = appointment.time_to;
    }
  });
  
  const timeFrom = latestEndTime;
  const fromMinutes = parseTimeToMinutes(timeFrom);
  const timeTo = formatMinutesToTime(fromMinutes + slotDurationMinutes);
  
  return { time_from: timeFrom, time_to: timeTo };
};

/**
 * Sort appointments by time slot
 */
export const sortAppointmentsByTime = (appointments: AppointmentWithTimeSlot[]): AppointmentWithTimeSlot[] => {
  return [...appointments].sort((a, b) => {
    // Primary sort: time_from
    if (a.time_from && b.time_from) {
      return a.time_from.localeCompare(b.time_from);
    }
    
    // If one has time_from and other doesn't, prioritize the one with time
    if (a.time_from && !b.time_from) return -1;
    if (!a.time_from && b.time_from) return 1;
    
    // Fallback to stop_sequence for backward compatibility
    if (a.stop_sequence && b.stop_sequence) {
      return a.stop_sequence - b.stop_sequence;
    }
    
    // Final fallback to id
    return (a.id || 0) - (b.id || 0);
  });
};

/**
 * Reassign time slots to appointments based on their order
 */
export const reassignTimeSlots = (
  appointments: AppointmentWithTimeSlot[],
  startTime: string = '08:00:00',
  slotDurationMinutes: number = 60
): AppointmentWithTimeSlot[] => {
  const startMinutes = parseTimeToMinutes(startTime);
  
  return appointments.map((appointment, index) => {
    const timeFromMinutes = startMinutes + (index * slotDurationMinutes);
    const timeToMinutes = timeFromMinutes + slotDurationMinutes;
    
    return {
      ...appointment,
      time_from: formatMinutesToTime(timeFromMinutes),
      time_to: formatMinutesToTime(timeToMinutes),
      stop_sequence: index + 1 // Maintain for backward compatibility
    };
  });
};

/**
 * Validate time slot format and logic
 */
export const validateTimeSlot = (slot: TimeSlot): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Check format
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
  
  if (!timeRegex.test(slot.time_from)) {
    errors.push('Invalid time_from format. Expected HH:MM:SS');
  }
  
  if (!timeRegex.test(slot.time_to)) {
    errors.push('Invalid time_to format. Expected HH:MM:SS');
  }
  
  // Check logic
  if (errors.length === 0) {
    const fromMinutes = parseTimeToMinutes(slot.time_from);
    const toMinutes = parseTimeToMinutes(slot.time_to);
    
    if (fromMinutes >= toMinutes) {
      errors.push('time_to must be after time_from');
    }
    
    if (toMinutes - fromMinutes > 8 * 60) { // 8 hours max
      errors.push('Time slot duration cannot exceed 8 hours');
    }
    
    if (toMinutes - fromMinutes < 15) { // 15 minutes min
      errors.push('Time slot duration must be at least 15 minutes');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
